// @ts-strict-ignore
import {Component, computed, inject, Input, output} from "@angular/core";
import {ChecklistItem} from "@models";
import {Translations} from "@translations";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {FormControl, FormGroup} from "@angular/forms";
import {ChecklistContextService} from "../services/checklist-context.service";
import {ChecklistAnswerService} from "../services/checklist-answer.service";
import {AssessablePanelMode} from "@common/components/assessment/utils";
import {AssessableUiService} from "../services/assessable-ui.service";
import {AssessableItemData} from "../assessable-item-template.directive";

@Component({
	selector: "sim-checklist-item",
	templateUrl: "./checklist-item.component.html",
	styleUrls: ["./checklist-item.component.scss"],
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class ChecklistItemComponent {
	protected readonly T = Translations;
	protected readonly AssessablePanelMode = AssessablePanelMode;

	@Input({required: true}) data: AssessableItemData<ChecklistItem>;
	@Input() isInArchive: boolean;
	@Input() compactLayout = false;

	onTimestampClicked = output<void>();

	protected store = inject(AssessableUiService);
	protected contextService = inject(ChecklistContextService);
	protected answerService = inject(ChecklistAnswerService);

	// signals
	public isItemInEditMode = computed(() => this.store.itemInEditMode() === this.data.item.assessableItemId);
	public isItemExpanded = computed(() => this.store.itemExpanded() === this.data.item.assessableItemId);

	// in edit mode, the answer control is not available in the form state service, so we create a temporary control
	// this control is irrelevant in other modes, because the answer control is always available then
	private _tempAnswerControl = new FormGroup({
		answer: new FormControl(),
		freeTextScore: new FormControl(0),
	});

	public get itemControl() {
		return this.contextService.findItemControl(this.data.item.assessableItemId);
	}

	public get answerItemControl() {
		return this.answerService.findItemControl(this.data.item.assessableItemId) ?? this._tempAnswerControl;
	}

	public mapToMultipleChoiceItem(item: ChecklistItem): {
		answer: any;
		options: string[];
	} {
		return {
			answer: this.answerItemControl?.controls.answer.value,
			options: item.selectableOptions,
		};
	}

	public handleAnswerChanged(value: unknown) {
		this.answerItemControl.controls.answer.setValue(value);
	}

	public handleMultipleChoiceAnswerChanged({checked, index}: {index: number; checked: boolean}) {
		let selectedOptions = Array.isArray(this.answerItemControl.value.answer) ? [...this.answerItemControl.value.answer] : [];

		if (checked && !selectedOptions.includes(index)) {
			selectedOptions.push(index);
		} else if (!checked && selectedOptions.includes(index)) {
			selectedOptions = selectedOptions.filter((value) => value !== index);
		}

		this.answerItemControl.controls.answer.setValue(selectedOptions);
	}

	public createRemoveItemEvent(index: number, groupIndex: number) {
		return {itemIndex: index, groupIndex: groupIndex};
	}
}
